/**
 * ADR-0221 §3.4 — Raise issue (`/app/issues/new`): `ActionItemCreate.tsx`
 * copied and trimmed to Initiative (preselected from `?initiative=`), Title,
 * Description, Team (preselected to the initiative's single team and
 * re-evaluated when the initiative changes) and Assigned to. Explicit submit.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import type { MemberCandidate } from "../components/MemberPicker.js";

type InitiativeOption = { id: string; name: string; lifecycle: string; teams: Array<{ id: string; name: string }> };

export type RaiseForm = { initiative_id: string; title: string; description: string; team_group_id: string; assignee_account_id: string };

/** The blur checks, all on submit. Pure, for the tests. */
export function validateRaise(f: RaiseForm): Partial<Record<keyof RaiseForm, string>> {
  const errs: Partial<Record<keyof RaiseForm, string>> = {};
  if (!f.title.trim()) errs.title = "Title is required.";
  else if (f.title.trim().length > 200) errs.title = "Title is at most 200 characters.";
  if (f.description.length > 10000) errs.description = "Description is at most 10000 characters.";
  return errs;
}

/** ADR-0223 §3.3: the idea an issue is raised from, carried in location state by *Make it → Issue*; anything else is ignored. */
export function fromIdeaState(state: unknown): { id: string; title: string; description: string } | null {
  const s = state as { from_idea?: { id?: unknown; title?: unknown; description?: unknown } } | null;
  const f = s?.from_idea;
  if (!f || typeof f.id !== "string" || typeof f.title !== "string") return null;
  return { id: f.id, title: f.title, description: typeof f.description === "string" ? f.description : "" };
}

/** The team an initiative gives an issue by default: its one team, else none (spec §3.4). */
export function defaultTeam(initiative: InitiativeOption | undefined): string {
  return initiative && initiative.teams.length === 1 ? initiative.teams[0]!.id : "";
}

export function TenantIssueCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("initiative") ?? "";
  // ADR-0223 §3.3: Make it → Issue arrives with the idea's title and description, and records the outcome on the idea in the same request.
  const fromIdea = fromIdeaState(useLocation().state);

  const [initiatives, setInitiatives] = useState<InitiativeOption[]>([]);
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<RaiseForm>({ initiative_id: preselected, title: fromIdea?.title ?? "", description: fromIdea?.description ?? "", team_group_id: "", assignee_account_id: "" });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof RaiseForm, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [inits, dir] = await Promise.all([
          apiFetch<{ initiatives: InitiativeOption[] }>("/api/tenant/initiatives"),
          apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
        ]);
        const active = inits.initiatives.filter((i) => i.lifecycle === "active");
        setInitiatives(active);
        setCandidates(dir.candidates);
        const chosen = active.find((i) => i.id === preselected);
        setForm((f) => ({ ...f, initiative_id: chosen ? chosen.id : "", team_group_id: defaultTeam(chosen) }));
      } catch (e) {
        setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [preselected]);

  const users = useMemo(() => candidates.filter((c) => c.kind === "user"), [candidates]);
  const groups = useMemo(() => candidates.filter((c) => c.kind === "group"), [candidates]);

  function update<K extends keyof RaiseForm>(key: K, value: RaiseForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function chooseInitiative(id: string) {
    const chosen = initiatives.find((i) => i.id === id);
    setForm((prev) => ({ ...prev, initiative_id: id, team_group_id: defaultTeam(chosen) }));
    setFieldErrors((prev) => ({ ...prev, initiative_id: undefined }));
  }

  async function submit() {
    setSubmitError(null);
    const errs = validateRaise(form);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0 || busy) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ issue: { id: string } }>("/api/tenant/issues", {
        method: "POST",
        body: {
          initiative_id: form.initiative_id || null,
          ...(fromIdea ? { from_idea_id: fromIdea.id } : {}),
          title: form.title.trim(),
          description: form.description.trim() || null,
          team_group_id: form.team_group_id || null,
          assignee_account_id: form.assignee_account_id || null,
        },
      });
      toast.push({ kind: "success", message: "Issue raised." });
      navigate(`/app/issues/${res.issue.id}`);
    } catch (e) {
      setSubmitError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const back = form.initiative_id ? `/app/initiatives/${form.initiative_id}` : "/app/issues";

  return (
    <PageLayout title="Raise issue">
      <div style={{ display: "grid", gap: 12 }}>
        {error && <ErrorState detail={error} />}
        {loading && !error && <LoadingState />}
        {!loading && !error && (
          <>
            <div className="card" style={{ display: "grid", gap: 10 }}>
              <label className="field">
                <span className="label">Initiative</span>
                <select className="select" value={form.initiative_id} onChange={(e) => chooseInitiative(e.target.value)}>
                  <option value="">No initiative</option>
                  {initiatives.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.initiative_id && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.initiative_id}</div>}
                {initiatives.length === 0 && <div className="help">No active initiative — the issue belongs to none.</div>}
              </label>
              <label className="field">
                <span className="label">Title *</span>
                <input
                  className="input"
                  value={form.title}
                  maxLength={200}
                  onChange={(e) => update("title", e.target.value)}
                  onBlur={() => {
                    if (!form.title.trim()) setFieldErrors((p) => ({ ...p, title: "Title is required." }));
                  }}
                  placeholder="What is in the way, in one line"
                />
                {fieldErrors.title && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.title}</div>}
              </label>
              <label className="field">
                <span className="label">Description</span>
                <textarea className="input" rows={4} value={form.description} maxLength={10000} onChange={(e) => update("description", e.target.value)} placeholder="What you know about it, and what it is doing to the initiative" />
                {fieldErrors.description && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.description}</div>}
              </label>
              <div className="inputRow" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label className="field">
                  <span className="label">Team</span>
                  <select className="select" value={form.team_group_id} onChange={(e) => update("team_group_id", e.target.value)}>
                    <option value="">None</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.display_name}
                      </option>
                    ))}
                  </select>
                  <div className="help">The team the issue is worked in. Defaults to the initiative's team when it has one.</div>
                </label>
                <label className="field">
                  <span className="label">Assigned to</span>
                  <select className="select" value={form.assignee_account_id} onChange={(e) => update("assignee_account_id", e.target.value)}>
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.display_name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            {submitError && <ErrorState detail={submitError} />}
            <div className="actions">
              <Link className="btn" to={back}>
                Cancel
              </Link>
              <button type="button" className="btn btnPrimary" disabled={busy} onClick={() => void submit()}>
                {busy ? "Raising…" : "Raise issue"}
              </button>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
